import {Dropdown, Form, Input, Select, Tabs} from 'antd';
import styles from './groupInstrumentForm.module.scss';
import shell from '../v2/pageShell.module.scss';
import {FormCard, spans, Hint} from '../v2/FormCard';
import {InstrumentActions} from '../v2/InstrumentActions';
import UploadImage from 'components/Startup/profile/companyImage';
import {isDev, supportedLanguages} from 'utils/constants';
import {useEffect, useState} from 'react';
import {Button} from '@dabafinance/react-components';
import {handleTranslateCollectionGroups} from 'pages/collections/CollectionGroups/create/actions';
import {useMutation, useQuery} from '@apollo/client';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';
import {GET_ASSET_CLASS_INSTRUMENTS} from 'graphql/queries/assetManagement';
import {useNavigate, useParams} from 'react-router-dom';
import _ from 'lodash';
import {toast} from 'react-toastify';
import {createInstrumentGroup} from '../actions';
import {GROUP_FINANCIAL_INSTRUMENTS} from 'graphql/mutations/assetManagement';
import {useSelector} from 'react-redux';
import {
  assetClassLabel,
  instrumentKeyLabel,
} from 'pages/AssetManagement/modules/instrumentKeys';

const languageLabel = code =>
  supportedLanguages.find(language => language.value === code)?.label ?? code;

/**
 * Create / update an instrument group.
 *
 * Was one flat 700px column on a white slab, where the translation
 * requirement only surfaced as a toast after submit and the base language and
 * translations were separated by unrelated fields. Now grouped into the same
 * cards the instrument flow uses, with the outstanding work stated up front
 * rather than on rejection.
 */
export const GroupInstrumentsForm = () => {
  const [imgUrl, setImgUrl] = useState(null);
  const [multiLang, setMultiLang] = useState([]);

  const [form] = Form.useForm();
  const title = Form.useWatch('title', form);
  const description = Form.useWatch('description', form);
  const language = Form.useWatch('language', form);
  const instruments = Form.useWatch('instruments', form);
  const {key, id, assetClass} = useParams();
  const navigate = useNavigate();
  const isUpdate = !!id;
  const defaultValues = useSelector(
    state => state?.assetClass?.groupedInstruments,
  );

  useEffect(() => {
    if (isUpdate) {
      form.setFieldsValue({
        title: defaultValues?.title,
        description: defaultValues?.description,
        language: defaultValues?.lang,
        riskLevel: defaultValues?.riskLevel,
        instruments: defaultValues?.instruments?.map(data => data?.id),
      });
      setImgUrl(defaultValues?.banner);
      setMultiLang(
        defaultValues?.multilang
          ?.filter(data => data?.language !== defaultValues.lang)
          ?.map(({title, description, language}) => ({
            title,
            description,
            language,
          })) ?? [],
      );
    }
  }, [isUpdate, defaultValues]);

  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
  );
  const {
    data,
    refetch: refetchInstrument,
    networkStatus,
    loading: loadingInstruments,
  } = useQuery(GET_ASSET_CLASS_INSTRUMENTS, {
    variables: {
      input: {
        explore: false,
        instrumentKey: key,
        assetClassKey: assetClass,
        page: 1,
        limit: 30,
      },
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    notifyOnNetworkStatusChange: true,
  });

  const [groupFinancialInstruments, {loading}] = useMutation(
    GROUP_FINANCIAL_INSTRUMENTS,
  );

  const handleChangeTranslatedValues = (key, value, name) => {
    const newFormValues = [...multiLang];
    newFormValues[key][name] = value;
    setMultiLang(newFormValues);
  };

  const translationProps = {
    sourceLang: language,
    targetText1: title,
    targetText2: description,
  };

  const handleSearch = _.debounce(async value => {
    try {
      refetchInstrument({
        input: {
          explore: false,
          instrumentKey: key,
          assetClassKey: assetClass,
          search: value,
          page: 1,
          limit: 30,
        },
      });
    } catch (error) {
      console.error('Error fetching instruments:', error);
    }
  }, 1200);

  // Stated on the form rather than discovered by submitting. At least one
  // translation is mandatory — the submit handler rejects without one.
  const outstanding = [
    !title && 'a title',
    !language && 'a base language',
    !instruments?.length && 'at least one instrument',
    !multiLang?.length && 'at least one translation',
    assetClass === 'FUNDS' &&
      !form.getFieldValue('riskLevel') &&
      'a risk level',
  ].filter(Boolean);

  const canTranslate = !loadingTranslation && !!title && !!description;

  const translateMenu = {
    items: supportedLanguages
      ?.filter(lang => lang.value !== language)
      .map(lang => ({
        label: `To ${lang.label}`,
        key: lang.value,
        onClick: () =>
          handleTranslateCollectionGroups(
            {...translationProps, targetLang: lang.value},
            multiLang,
            setMultiLang,
            translateText,
          ),
      })),
  };

  // antd 4.24 deprecates Tabs.TabPane in favour of `items`, and the old
  // `multiLang?.length && …` child rendered a bare 0 into the tab strip when
  // no translation existed yet.
  const tabItems = [
    {
      key: 'BaseLanguage',
      label: language ? languageLabel(language) : 'Base language',
      children: (
        <>
          <div className={spans.full}>
            <Form.Item
              name="title"
              label="Title"
              rules={[{required: true, message: 'Title is Required'}]}>
              <Input placeholder="High-yield West African bonds" />
            </Form.Item>
          </div>
          <div className={spans.full}>
            <Form.Item label="Description" name="description">
              <Input.TextArea
                rows={4}
                placeholder="What ties these instruments together, in a sentence or two."
              />
            </Form.Item>
          </div>
        </>
      ),
    },
    ...(multiLang ?? []).map((data, index) => ({
      key: data?.language,
      label: languageLabel(data?.language),
      children: (
        <>
          <div className={spans.full}>
            <Form.Item label="Translated title">
              <Input
                value={data?.title}
                onChange={e =>
                  handleChangeTranslatedValues(index, e.target.value, 'title')
                }
              />
            </Form.Item>
          </div>
          <div className={spans.full}>
            <Form.Item label="Translated description">
              <Input.TextArea
                rows={4}
                value={data?.description}
                onChange={e =>
                  handleChangeTranslatedValues(
                    index,
                    e.target.value,
                    'description',
                  )
                }
              />
            </Form.Item>
          </div>
        </>
      ),
    })),
  ];

  return (
    <div>
      <div className={shell.head}>
        <div className={shell.headTop}>
          <div>
            <button
              type="button"
              className={shell.back}
              onClick={() => navigate(-1)}>
              ← Back
            </button>
            <h1 className={shell.title}>
              {isUpdate ? 'Update' : 'New'} instrument group
            </h1>
            <div className={shell.sub}>
              {assetClassLabel(assetClass)} · {instrumentKeyLabel(key)}
              {instruments?.length
                ? ` · ${instruments.length} instrument${
                    instruments.length === 1 ? '' : 's'
                  }`
                : ''}
            </div>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={values => {
          if (!multiLang.length) {
            return toast.error('Please Provide a Translation');
          }
          createInstrumentGroup(
            groupFinancialInstruments,
            values,
            imgUrl,
            multiLang,
            assetClass,
            key,
            navigate,
            id,
          );
        }}>
        <FormCard
          title="Presentation"
          description="How the group appears wherever it is surfaced to investors">
          <div className={spans.full}>
            <div className={styles.image}>
              <UploadImage
                large
                previewUrl={imgUrl}
                setImgUrl={setImgUrl}
                isACL={true}
                label="Upload Group Banner"
                folder={'groups'}
                bucket={
                  isDev
                    ? 'dabafinancialinstruments-nonprod'
                    : 'dabafinancialinstruments-prod'
                }
              />
            </div>
          </div>
          <div className={spans.half}>
            <Form.Item
              rules={[{required: true, message: 'Language is Required'}]}
              name="language"
              label="Base language">
              <Select disabled={isUpdate} placeholder="Select a language">
                {supportedLanguages.map(lang => (
                  <Select.Option key={lang.value}>{lang.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            {isUpdate && (
              <Hint>
                Fixed once the group exists — create a new group to change it.
              </Hint>
            )}
          </div>
          {assetClass === 'FUNDS' && (
            <div className={spans.half}>
              <Form.Item
                rules={[{required: true, message: 'Risk level is required'}]}
                name="riskLevel"
                label="Risk level">
                <Select allowClear placeholder="Select a risk level">
                  <Select.Option value="CONSERVATIVE">
                    Conservative
                  </Select.Option>
                  <Select.Option value="MODERATE">Moderate</Select.Option>
                  <Select.Option value="AGGRESSIVE">Aggressive</Select.Option>
                </Select>
              </Form.Item>
            </div>
          )}
        </FormCard>

        <FormCard
          title="Copy"
          description="Written once in the base language, then translated — every supported language is shown to somebody">
          <div className={spans.full}>
            <Tabs
              className={styles.tabs}
              items={tabItems}
              tabBarExtraContent={
                language && (
                  <Dropdown
                    disabled={!canTranslate}
                    trigger="click"
                    menu={translateMenu}>
                    <div>
                      <Button
                        label={
                          loadingTranslation ? 'Translating…' : 'Translate'
                        }
                        disabled={!canTranslate}
                      />
                    </div>
                  </Dropdown>
                )
              }
            />
            {!multiLang?.length && (
              <Hint>
                A translation is required. Fill in the title and description,
                then use Translate.
              </Hint>
            )}
          </div>
        </FormCard>

        <FormCard
          title="Instruments"
          description="Search by name — the list loads 30 at a time">
          <div className={spans.full}>
            <Form.Item
              name="instruments"
              rules={[{required: true, message: 'This is Required'}]}
              label="Instruments in this group">
              <Select
                showSearch
                mode="multiple"
                onSearch={handleSearch}
                filterOption={false}
                loading={loadingInstruments}
                disabled={networkStatus === 1}
                placeholder="Search for Instrument"
                allowClear>
                {data?.getFinancialInstruments?.data?.map(instrument => (
                  <Select.Option key={instrument?.id}>
                    {instrument?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </FormCard>

        <InstrumentActions
          remaining={outstanding.length}
          disabled={loading}
          label={
            loading ? 'Saving…' : isUpdate ? 'Save changes' : 'Create group'
          }
        />
      </Form>
    </div>
  );
};
